import React, { useEffect } from 'react';
import { Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useLazyQuery } from '@apollo/client';

import KEY_CLASSROOM_ENGAGEMENT_QUERY from 'graph/reports/key-classroom-engagement-report-query';
import useReportQueryParams from '../../../../hooks/use-report-query-params';
import EngagementOverviewTableCard from '../EngagementTableCards/EngagementOverviewTableCard';
import ClassroomSpotCheckChartCard from './ClassroomSpotCheckChartCard';
import ScheduledActivityTableCard from '../EngagementTableCards/ScheduledActivityTableCard';
import EnrichmentActivityTableCard from '../EngagementTableCards/EnrichmentActivityTableCard';
import TeacherCreatedActivitiesTableCard from '../EngagementTableCards/TeacherCreatedActivitiesTableCard';
import SpotCheckTableCard from '../EngagementTableCards/SpotCheckTableCard';
import EngagementOverviewChartCards from './EngagementOverviewChartCards';
import ClassroomScheduledActivityChartCard from './ClassroomScheduledActivityChartCard';
import ClassroomEnrichmentActivityChartCard from './ClassroomEnrichmentActivityChartCard';
import { getQueryVariables } from 'components/ReportsModule/utils/report-param-utils';
import { sortAlphabeticallyByKeys } from 'utilities/sort-util';
import {
  KeyClassroomEngagementReportResult,
  KeyClassroomEngagementStudentResult
} from 'types/schema-generated-types';
import { ReportView } from '../../../../types';
import ClassroomAssessmentAcceleratorTableCard from '../EngagementTableCards/ClassroomAssessmentAcceleratorTableCard';
import ClassroomAssessmentAcceleratorChart from './ClassroomAssessmentAcceleratorChartCard';

const EngagementCards: React.FC = () => {
  const [{ entityId, entityType, dateRange, viewAs }] = useReportQueryParams();
  const { t } = useTranslation('Reports');
  const [fetchReport, { data, loading }] = useLazyQuery<{
    keyClassroomEngagementReport: KeyClassroomEngagementReportResult;
  }>(KEY_CLASSROOM_ENGAGEMENT_QUERY);

  const sortByStudent = (
    a: KeyClassroomEngagementStudentResult,
    b: KeyClassroomEngagementStudentResult
  ): number => sortAlphabeticallyByKeys(['student.firstName', 'student.lastName'], a, b);

  const reportData =
    data?.keyClassroomEngagementReport?.students.slice()?.sort(sortByStudent) ?? [];

  useEffect(() => {
    fetchReport({ variables: getQueryVariables({ entityId, entityType, dateRange }) });
  }, [fetchReport, entityId, entityType, dateRange]);

  const chartView = (
    <>
      <EngagementOverviewChartCards reportData={reportData} loading={loading} />
      <ClassroomSpotCheckChartCard reportData={reportData} loading={loading} />
      <ClassroomScheduledActivityChartCard reportData={reportData} loading={loading} />
      <ClassroomAssessmentAcceleratorChart
        loading={loading}
        classroomReport={data?.keyClassroomEngagementReport!}
      />
      <ClassroomEnrichmentActivityChartCard reportData={reportData} loading={loading} />
    </>
  );

  const tableView = (
    <>
      <EngagementOverviewTableCard reportData={[...reportData]} loading={loading} />
      <SpotCheckTableCard reportData={[...reportData]} loading={loading} />
      <ScheduledActivityTableCard reportData={[...reportData]} loading={loading} />
      <ClassroomAssessmentAcceleratorTableCard
        loading={loading}
        classroomReport={{ ...data?.keyClassroomEngagementReport } as any}
      />
      <EnrichmentActivityTableCard reportData={[...reportData]} loading={loading} />
      <TeacherCreatedActivitiesTableCard reportData={[...reportData]} loading={loading} />
    </>
  );

  const REPORT_VIEW_MAP: { [key in ReportView]?: any } = {
    [ReportView.CHART]: chartView,
    [ReportView.TABLE]: tableView
  };

  return (
    <section>
      <Typography variant="h3">{t('Engagement')}</Typography>
      {REPORT_VIEW_MAP[viewAs]}
    </section>
  );
};

export default EngagementCards;
